// licenses_manager.jsx
// The licenses table and the license drawer behind it: every computer the
// license was activated on, the evidence that two records are the same
// computer, the activations the device limit refused, and the two fixes.
//
// Why duplicates happen: the AutoLey server creates a new device record on
// every activation and has no machine id, so reinstalling the app on the same
// computer takes a second slot while the old record (whose token nothing uses
// any more) keeps counting. The backend compares what it knows about each
// record (computer name, OS, activation IP, when each was last seen) and flags
// likely duplicates; the admin looks at the comparison and decides.
//
// The two fixes, both recorded in AutoLey's audit log with the admin's id:
//  • Free the slot — deactivate the stale record. The right fix for a true
//    duplicate: the license keeps its allowance and the computer keeps working
//    on its newer record.
//  • Grant another activation — raise the allowance by one. For when it isn't
//    clearly a duplicate, or the lawyer genuinely needs another computer.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  KeyRound, Search, RefreshCw, Monitor, Mail, Calendar, ShieldAlert, Ban, Copy, Plus, Minus,
  History, Laptop, ArrowRight, Unplug, Info, CheckCircle2, Loader2,
} from 'lucide-react';
import { useAutoLey } from '../../authentication_components/autoleyContext';
import {
  Section, Modal, Field, Button, Loading, EmptyState, Pagination, Badge, LicenseFlags, LicenseStatusBadge,
  ConfidenceBadge, SlotMeter, inputClass, controlClass, fmtDate, fmtDateTime, fmtAgo, fmtGap, osLabel, accountName,
  REASON_LABELS, actorLabel, machineLabel,
} from './autoley_ui';

const PAGE_SIZE = 25;

const lowerFirst = (s) => s.charAt(0).toLowerCase() + s.slice(1);

const FILTERS = [
  { id: 'needs_review', label: 'Needs review' },
  { id: 'blocked', label: 'Locked out' },
  { id: 'duplicates', label: 'Duplicate installs' },
  { id: 'at_limit', label: 'At device limit' },
  { id: 'all', label: 'All licenses' },
];

export default function LicensesManager({ focusLicenseId, onFocusHandled, initialFilter = 'all' }) {
  const { fetchLicenses } = useAutoLey();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(initialFilter);
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState({ licenses: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { setFilter(initialFilter); setOffset(0); }, [initialFilter]);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await fetchLicenses({ search: search || undefined, filter, status: status || undefined, limit: PAGE_SIZE, offset });
      setData({ licenses: result?.licenses || [], total: result?.total || 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load licenses');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchLicenses, search, filter, status, offset]);

  useEffect(() => { load(); }, [load]);

  // Another tab asked for one license to be opened.
  useEffect(() => {
    if (focusLicenseId) {
      setSelectedId(focusLicenseId);
      onFocusHandled?.();
    }
  }, [focusLicenseId, onFocusHandled]);

  const refresh = () => { setRefreshing(true); load({ silent: true }); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">Licenses</h2>
          <p className="text-sm text-slate-500">Which licenses are activated on which computers — and which ones look like the same computer twice.</p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Section title="Licenses" icon={KeyRound} className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Email, name, computer name, key (whole or last 4)…"
              className={`${inputClass} pl-9`}
            />
          </div>
          <select value={filter} onChange={(e) => { setOffset(0); setFilter(e.target.value); }} className={`${controlClass} w-48`}>
            {FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <select value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }} className={`${controlClass} w-36`}>
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>

        {loading && data.licenses.length === 0 ? (
          <Loading label="Loading licenses…" />
        ) : data.licenses.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={KeyRound}
              title={filter === 'needs_review' || filter === 'blocked' || filter === 'duplicates' ? 'Nothing to review' : 'No licenses match'}
              hint={filter === 'all' ? 'Clear the search or the status filter.' : 'No license matches this filter right now.'}
              action={<Button variant="secondary" onClick={() => { setSearchInput(''); setFilter('all'); setStatus(''); }}>Show all licenses</Button>}
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Account</th>
                    <th className="px-4 py-3">Key</th>
                    <th className="px-4 py-3">Computers</th>
                    <th className="px-4 py-3">Last activation</th>
                    <th className="px-4 py-3">Review</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.licenses.map((l) => (
                    <tr key={l.license_id} className="cursor-pointer hover:bg-blue-50/40" onClick={() => setSelectedId(l.license_id)}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{accountName(l.account)}</div>
                        <div className="text-xs text-slate-400">{l.account?.email}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-600">…{l.key_hint}</td>
                      <td className="px-4 py-3"><SlotMeter active={l.active_devices} max={l.max_devices} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(l.last_activation_at)}</td>
                      <td className="px-4 py-3"><LicenseFlags license={l} /></td>
                      <td className="px-4 py-3 text-right"><span className="text-xs font-semibold text-blue-700">Open</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} noun="license" />
          </>
        )}
      </Section>

      <LicenseDetailModal
        licenseId={selectedId}
        onClose={() => setSelectedId(null)}
        onMutated={() => load({ silent: true })}
      />
    </div>
  );
}

// ─── License drawer ──────────────────────────────────────────────────────────

export function LicenseDetailModal({ licenseId, onClose, onMutated }) {
  const { getLicense } = useAutoLey();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(null); // { device, keep, reason }

  // Parents pass onClose inline; keeping it out of load's dependencies stops
  // every parent render from refetching the license.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const load = useCallback(async () => {
    if (!licenseId) return;
    setLoading(true);
    try {
      setDetail(await getLicense(licenseId));
    } catch (err) {
      toast.error(err.message || 'Failed to load the license');
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [getLicense, licenseId]);

  useEffect(() => {
    setDetail(null);
    if (licenseId) load();
  }, [licenseId, load]);

  const devicesById = useMemo(
    () => Object.fromEntries((detail?.devices || []).map((d) => [d.id, d])),
    [detail],
  );

  const afterMutation = () => { load(); onMutated?.(); };

  const askDeactivate = (device, { keep, reason } = {}) => setDeactivating({ device, keep, reason });

  return (
    <>
      <Modal
        open={!!licenseId}
        title={detail ? accountName(detail.account) : 'License'}
        subtitle={detail ? `${detail.account?.email} · key …${detail.key_hint}` : undefined}
        onClose={onClose}
        wide
      >
        {loading && !detail ? (
          <Loading label="Loading license…" />
        ) : !detail ? (
          <EmptyState title="License not available" />
        ) : (
          <div className="space-y-5">
            <LicenseHeader license={detail} />

            {detail.blocked && <BlockedBanner license={detail} devicesById={devicesById} />}

            {detail.status === 'active' && detail.duplicates.length > 0 && (
              <section className="space-y-3">
                <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
                  <Copy className="h-4 w-4 text-amber-600" /> Same computer activated twice?
                </h4>
                {detail.duplicates.map((s) => (
                  <DuplicateCard
                    key={s.device_id}
                    suspect={s}
                    stale={devicesById[s.device_id]}
                    keep={devicesById[s.duplicate_of]}
                    onFree={() => askDeactivate(devicesById[s.device_id], {
                      keep: devicesById[s.duplicate_of],
                      reason: `Duplicate install on ${devicesById[s.duplicate_of]?.name || 'the same computer'} (newer record kept)`,
                    })}
                  />
                ))}
              </section>
            )}

            {detail.refused_attempts.length > 0 && (
              <RefusedAttempts
                attempts={detail.refused_attempts}
                devicesById={devicesById}
                licenseActive={detail.status === 'active'}
                blocked={detail.blocked}
                onFree={(device, attempt) => askDeactivate(device, {
                  reason: `Reinstall on ${attempt.name}: freeing its old record so it can activate again`,
                })}
              />
            )}

            {detail.status === 'active' && <AllowancePanel license={detail} onMutated={afterMutation} />}

            <DevicesTable
              devices={detail.devices}
              suspects={detail.duplicates}
              licenseActive={detail.status === 'active'}
              onDeactivate={(d) => askDeactivate(d)}
            />

            <HistoryList entries={detail.history} devicesById={devicesById} />
          </div>
        )}
      </Modal>

      <DeactivateDialog
        request={deactivating}
        onCancel={() => setDeactivating(null)}
        onDone={() => { setDeactivating(null); afterMutation(); }}
      />
    </>
  );
}

function LicenseHeader({ license }) {
  return (
    <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-4">
      <Detail icon={Mail} label="Account" value={license.account?.email} sub={license.account?.name || undefined} />
      <Detail icon={KeyRound} label="License" value={<LicenseStatusBadge status={license.status} />}
        sub={`${license.source === 'stripe' ? 'Bought online' : 'Created by support'}${license.note ? ` · ${license.note}` : ''}`} />
      <div>
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Monitor className="h-3.5 w-3.5 text-slate-400" /> Computers
        </div>
        <div className="mt-1"><SlotMeter active={license.active_devices} max={license.max_devices} /></div>
        <p className="text-xs text-slate-400">{license.at_limit ? 'No free slot' : `${license.max_devices - license.active_devices} free`}</p>
      </div>
      <Detail icon={Calendar} label="Created" value={fmtDate(license.created_at)}
        sub={license.key_emailed_at ? `key emailed ${fmtDate(license.key_emailed_at)}` : 'key not emailed'} />
    </div>
  );
}

function Detail({ icon: Icon, label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5 text-slate-400" /> {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-medium text-slate-800" title={typeof value === 'string' ? value : undefined}>{value}</div>
      {sub && <p className="truncate text-xs text-slate-400" title={sub}>{sub}</p>}
    </div>
  );
}

function BlockedBanner({ license, devicesById }) {
  const last = license.refused_attempts[0];
  const match = last?.matches_device_id ? devicesById[last.matches_device_id] : null;
  return (
    <div className="flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
      <Ban className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
      <div className="text-sm text-rose-900">
        <p className="font-semibold">This lawyer is locked out.</p>
        <p className="mt-0.5">
          {last ? <>Activating <b>{last.name}</b> ({osLabel(last.os)}) was refused {fmtAgo(last.at)} because all {license.max_devices} slots are taken, </> : 'An activation was refused for the device limit, '}
          and nothing on the license has changed since.
          {match
            ? <> It looks like <b>{match.name}</b>, which is already on the license — a reinstall. Freeing its old record lets it activate again.</>
            : license.duplicates.length > 0
              ? ' One of the slots looks like a duplicate install (below): freeing it lets the new computer activate.'
              : ' Free a slot the lawyer no longer uses, or grant another activation.'}
        </p>
      </div>
    </div>
  );
}

// DuplicateCard — the two records side by side, matching values marked, with
// the fix. The stale record is the one seen first; the other one is in use.
function DuplicateCard({ suspect, stale, keep, onFree }) {
  if (!stale || !keep) return null;
  const reasons = new Set(suspect.reasons);
  const rows = [
    { label: 'Computer name', a: stale.name, b: keep.name, same: reasons.has('same_name') },
    { label: 'System', a: `${osLabel(stale.os)} · v${stale.app_version}`, b: `${osLabel(keep.os)} · v${keep.app_version}`, same: reasons.has('same_os') },
    { label: 'Machine ID', a: machineLabel(stale.machine_id), b: machineLabel(keep.machine_id), same: reasons.has('same_machine'), mono: true },
    { label: 'IP at activation', a: stale.activation_ip || 'not recorded', b: keep.activation_ip || 'not recorded', same: reasons.has('same_ip'), mono: true },
    { label: 'Activated', a: fmtDateTime(stale.activated_at), b: fmtDateTime(keep.activated_at), same: reasons.has('activated_close') },
    { label: 'Last seen', a: fmtAgo(stale.last_seen_at), b: fmtAgo(keep.last_seen_at), same: false },
  ];

  return (
    <div className={`rounded-xl border p-4 ${suspect.confidence === 'likely' ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ConfidenceBadge confidence={suspect.confidence} />
          <span className="text-xs text-slate-500">
            activated {fmtGap(stale.activated_at, keep.activated_at)} apart
          </span>
        </div>
        <Button onClick={onFree}>
          <Unplug className="h-4 w-4" /> Free the older record's slot
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-40 py-1.5 pr-3" />
              <th className="py-1.5 pr-3">Older record <span className="font-normal normal-case text-slate-400">— stale</span></th>
              <th className="py-1.5"><ArrowRight className="inline h-3 w-3" /> Newer record <span className="font-normal normal-case text-slate-400">— in use, kept</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-slate-100">
                <td className="py-1.5 pr-3 text-xs font-semibold text-slate-500">{r.label}</td>
                <td className={`py-1.5 pr-3 ${r.mono ? 'font-mono text-xs' : ''} text-slate-700`}>
                  {r.a} {r.same && <CheckCircle2 className="ml-1 inline h-3.5 w-3.5 text-emerald-600" aria-label="matches" />}
                </td>
                <td className={`py-1.5 ${r.mono ? 'font-mono text-xs' : ''} text-slate-700`}>{r.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="mt-3 flex flex-wrap gap-1.5">
        {suspect.reasons.map((r) => (
          <li key={r}><Badge className="bg-white text-slate-600 ring-slate-200">{REASON_LABELS[r] || r}</Badge></li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-500">
        Freeing the older record gives its slot back; the computer keeps working on the newer one. If both records are in fact
        different computers, use <b>Grant another activation</b> below instead.
      </p>
    </div>
  );
}

function RefusedAttempts({ attempts, devicesById, licenseActive, blocked, onFree }) {
  return (
    <section className="rounded-xl border border-slate-200 p-4">
      <h4 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
        <ShieldAlert className="h-4 w-4 text-rose-600" /> Refused activations <span className="font-normal normal-case text-slate-400">(last 30 days)</span>
      </h4>
      <p className="mb-3 text-xs text-slate-500">
        Attempts the device limit turned away. Compare them with the computers below.
        {!blocked && licenseActive && ' The license has changed since the last one, so the lawyer can try again.'}
      </p>
      <ul className="divide-y divide-slate-100">
        {attempts.map((a, i) => {
          const match = a.matches_device_id ? devicesById[a.matches_device_id] : null;
          return (
            <li key={`${a.at}-${i}`} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-slate-800">
                  {a.name} <span className="font-normal text-slate-500">· {osLabel(a.os)} · v{a.app_version}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {fmtDateTime(a.at)} · IP <span className="font-mono">{a.ip || 'not recorded'}</span>
                  {a.machine_id && <> · machine <span className="font-mono">{machineLabel(a.machine_id)}</span></>}
                </p>
                {match && (
                  <p className="mt-1 text-xs text-amber-800">
                    Looks like <b>{match.name}</b> already on this license ({a.match_reasons.map((r) => lowerFirst(REASON_LABELS[r] || r)).join(', ')})
                    — a reinstall on that computer.
                  </p>
                )}
              </div>
              {match && match.active && licenseActive && (
                <Button variant="secondary" onClick={() => onFree(match, a)}>
                  <Unplug className="h-4 w-4" /> Free {match.name}'s old record
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// AllowancePanel — "grant another activation" is the +1; −1 undoes one.
function AllowancePanel({ license, onMutated }) {
  const { setMaxDevices } = useAutoLey();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(''); }, [license.license_id]);

  const change = async (delta) => {
    if (!reason.trim()) {
      toast.error('Write a reason first — it is kept in the license history');
      return;
    }
    setBusy(true);
    try {
      const result = await setMaxDevices(license.license_id, {
        from: license.max_devices,
        maxDevices: license.max_devices + delta,
        reason: reason.trim(),
      });
      toast.success(delta > 0
        ? `Granted another activation — the license now allows ${result?.max_devices} computers`
        : `The license now allows ${result?.max_devices} computers`);
      setReason('');
      onMutated?.();
    } catch (err) {
      toast.error(err.message || 'Failed to change the allowance');
      // A 409 means the allowance moved under us: show the current one.
      if (err.status === 409) onMutated?.();
    } finally {
      setBusy(false);
    }
  };

  const canLower = license.max_devices > Math.max(1, license.active_devices);

  return (
    <section className="rounded-xl border border-slate-200 p-4">
      <h4 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
        <Monitor className="h-4 w-4 text-blue-700" /> Activations allowed: {license.max_devices}
      </h4>
      <p className="mb-3 text-xs text-slate-500">
        Each purchase includes 2 computers. Granting one more lets the lawyer activate another computer right away; it stays until
        someone lowers it again.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Reason" required>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Reinstalled on the same computer; old record kept for now"
            maxLength={500}
            className={inputClass}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => change(1)} disabled={busy || license.max_devices >= 100}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Grant another activation
          </Button>
          <Button variant="secondary" onClick={() => change(-1)} disabled={busy || !canLower}
            title={canLower ? 'Allow one computer fewer' : 'Can’t go below the computers active now'}>
            <Minus className="h-4 w-4" /> One fewer
          </Button>
        </div>
      </div>
    </section>
  );
}

function DevicesTable({ devices, suspects, licenseActive, onDeactivate }) {
  const staleIds = new Set(suspects.map((s) => s.device_id));
  return (
    <section className="rounded-xl border border-slate-200">
      <h4 className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-bold uppercase tracking-wide text-slate-700">
        <Laptop className="h-4 w-4 text-blue-700" /> Computers on this license
      </h4>
      {devices.length === 0 ? (
        <div className="p-4"><EmptyState icon={Laptop} title="Never activated" hint="The key hasn't been used on any computer yet." /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Computer</th>
                <th className="px-4 py-2.5">IP at activation</th>
                <th className="px-4 py-2.5">Activated</th>
                <th className="px-4 py-2.5">Last seen</th>
                <th className="px-4 py-2.5">State</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {devices.map((d) => (
                <tr key={d.id} className={staleIds.has(d.id) ? 'bg-amber-50/50' : d.active ? '' : 'text-slate-400'}>
                  <td className="px-4 py-2.5">
                    <div className={`font-semibold ${d.active ? 'text-slate-800' : 'text-slate-500'}`}>{d.name}</div>
                    <div className="text-xs text-slate-400">{osLabel(d.os)} · v{d.app_version} · <span className="font-mono">{d.id.slice(-8)}</span></div>
                    <div className="text-xs text-slate-400" title="Identifies the computer; a reinstall on it gets this record back">
                      machine <span className="font-mono">{machineLabel(d.machine_id)}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">{d.activation_ip || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">{fmtDateTime(d.activated_at)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5" title={fmtDateTime(d.last_seen_at)}>{fmtAgo(d.last_seen_at)}</td>
                  <td className="px-4 py-2.5">
                    {d.active ? (
                      staleIds.has(d.id)
                        ? <Badge icon={Copy} className="bg-amber-50 text-amber-800 ring-amber-200">Active · looks stale</Badge>
                        : <Badge icon={CheckCircle2} className="bg-emerald-50 text-emerald-700 ring-emerald-200">Active</Badge>
                    ) : (
                      <span className="text-xs">
                        Deactivated {fmtDate(d.deactivated_at)}
                        {d.deactivated_by && <span className="block text-slate-400">by {actorLabel(d.deactivated_by)}</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {d.active && licenseActive && (
                      <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => onDeactivate(d)}>
                        <Unplug className="h-3.5 w-3.5" /> Deactivate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── History ─────────────────────────────────────────────────────────────────

function describeEntry(e, devicesById) {
  const d = e.detail || {};
  const deviceName = d.name || devicesById[e.device_id]?.name || 'a computer';
  switch (e.action) {
    case 'license.created': return `License created (${d.max_devices ?? '?'} computers, ${d.source || 'manual'})`;
    case 'license.purchased': return 'License bought online';
    case 'license.key_sent': return 'Activation key emailed';
    case 'license.revoked': return `License revoked${d.reason ? ` — ${d.reason}` : ''}`;
    case 'license.max_devices_changed': return `Allowance ${d.from} → ${d.to}${d.reason ? ` — ${d.reason}` : ''}`;
    case 'device.activated': return `Activated ${deviceName} (${osLabel(d.os)}) from ${d.ip || 'unknown IP'}`;
    case 'device.reactivated': return `Reactivated ${deviceName} on the same computer (reinstall) from ${d.ip || 'unknown IP'} — no extra slot used`;
    case 'device.activation_refused': return `Refused ${deviceName} (${osLabel(d.os)}) from ${d.ip || 'unknown IP'} — ${d.active_devices ?? '?'}/${d.max_devices ?? '?'} in use`;
    case 'device.deactivated': return `Deactivated ${deviceName}${d.reason ? ` — ${d.reason}` : ''}`;
    case 'subscription.changed': return `Research plan changed${d.tier ? ` (${d.tier})` : ''}`;
    default: return e.action;
  }
}

function HistoryList({ entries, devicesById }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? entries : entries.slice(0, 8);
  return (
    <section className="rounded-xl border border-slate-200 p-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
        <History className="h-4 w-4 text-blue-700" /> History
      </h4>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-400">No history recorded.</p>
      ) : (
        <>
          <ol className="space-y-2">
            {shown.map((e) => (
              <li key={e.id} className="flex gap-3 text-sm">
                <span className="w-36 shrink-0 text-xs text-slate-400">{fmtDateTime(e.at)}</span>
                <span className="min-w-0 text-slate-700">
                  {describeEntry(e, devicesById)}
                  <span className="block text-xs text-slate-400">by {actorLabel(e.actor)}</span>
                </span>
              </li>
            ))}
          </ol>
          {entries.length > 8 && (
            <button onClick={() => setExpanded((x) => !x)} className="mt-3 text-xs font-semibold text-blue-700 hover:underline">
              {expanded ? 'Show less' : `Show all ${entries.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ─── Deactivate confirmation ─────────────────────────────────────────────────

function DeactivateDialog({ request, onCancel, onDone }) {
  const { deactivateDevice } = useAutoLey();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(request?.reason || ''); }, [request]);

  if (!request) return null;
  const { device, keep } = request;

  const submit = async () => {
    setBusy(true);
    try {
      await deactivateDevice(device.id, { reason: reason.trim() });
      toast.success(`Freed ${device.name}'s slot`);
      onDone();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate the device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Deactivate ${device.name}?`}
      subtitle={`${osLabel(device.os)} · activated ${fmtDate(device.activated_at)} · last seen ${fmtAgo(device.last_seen_at)}`}
      onClose={busy ? () => {} : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />} Free this slot
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-slate-600">
        <p>
          This frees one of the license's slots so another computer can activate. The record's sign-in stops working at once.
        </p>
        {keep ? (
          <p className="flex gap-2 rounded-lg bg-slate-50 p-3 text-xs">
            <Info className="h-4 w-4 shrink-0 text-blue-700" />
            <span>The computer keeps working on its newer record, <b>{keep.name}</b> (activated {fmtDate(keep.activated_at)}, last seen {fmtAgo(keep.last_seen_at)}).</span>
          </p>
        ) : (
          <p className="flex gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
            <Info className="h-4 w-4 shrink-0" />
            <span>If AutoLey is still in use on this computer, it will ask for the activation key again the next time it goes online.</span>
          </p>
        )}
        <Field label="Reason" hint="Kept in the license history with your admin id.">
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className={inputClass} />
        </Field>
      </div>
    </Modal>
  );
}

